import React, { useState, useEffect, useRef } from 'react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocationOn as LocationOnIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Slider,
  Switch,
  Rating,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from '@mui/material';

// Enhanced Input Components
export function PhoneInput({
  value, onChange, placeholder, ...props
}) {
  return (
    <TextField
      type="tel"
      value={value}
      onChange={onChange}
      placeholder={placeholder || '(123) 456-7890'}
      inputProps={{
        pattern: '[0-9]{3}-[0-9]{3}-[0-9]{4}',
      }}
      {...props}
    />
  );
}

export function CurrencyInput({
  value, onChange, prefix = '$', ...props
}) {
  return (
    <TextField
      type="number"
      value={value}
      onChange={onChange}
      InputProps={{
        startAdornment: <span style={{ marginRight: 8 }}>{prefix}</span>,
      }}
      inputProps={{
        step: 0.01,
        min: 0,
      }}
      {...props}
    />
  );
}

export function EnhancedSlider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  showValue = true,
  marks = false,
  ...props
}) {
  return (
    <Box sx={{ px: 2 }}>
      <Slider
        value={value || min}
        onChange={(e, newValue) => onChange({ target: { value: newValue } })}
        min={min}
        max={max}
        step={step}
        marks={marks}
        valueLabelDisplay={showValue ? 'auto' : 'off'}
        {...props}
      />
      {showValue && (
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        Value:
        {' '}
        {value || min}
      </Typography>
      )}
    </Box>
  );
}

export function EnhancedRating({
  value,
  onChange,
  max = 5,
  allowHalf = true,
  showText = true,
  ...props
}) {
  return (
    <Box>
      <Rating
        value={value || 0}
        onChange={(e, newValue) => onChange({ target: { value: newValue } })}
        max={max}
        precision={allowHalf ? 0.5 : 1}
        {...props}
      />
      {showText && (
      <Typography variant="caption" sx={{ ml: 1 }}>
        {value || 0}
        {' '}
        of
        {max}
      </Typography>
      )}
    </Box>
  );
}

export function ColorPicker({
  value,
  onChange,
  swatches = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3'],
  ...props
}) {
  return (
    <Box>
      <input
        type="color"
        value={value || '#1976d2'}
        onChange={(e) => onChange({ target: { value: e.target.value } })}
        style={{
          width: '100%',
          height: 40,
          border: '1px solid #ccc',
          borderRadius: 4,
          cursor: 'pointer',
        }}
        {...props}
      />
      <Box sx={{
        mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap',
      }}
      >
        {swatches.map((color) => (
          <Box
            key={color}
            onClick={() => onChange({ target: { value: color } })}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: color,
              borderRadius: 1,
              cursor: 'pointer',
              border: value === color ? '2px solid #000' : '1px solid #ccc',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export function ToggleSwitch({
  value,
  onChange,
  size = 'medium',
  color = 'primary',
  label,
  ...props
}) {
  return (
    <FormControlLabel
      control={(
        <Switch
          checked={value || false}
          onChange={(e) => onChange({ target: { value: e.target.checked } })}
          size={size}
          color={color}
          {...props}
        />
    )}
      label={label}
    />
  );
}

export function CheckboxGroup({
  value = [],
  onChange,
  options = [],
  layout = 'vertical',
  showSelectAll = false,
  label,
  ...props
}) {
  const handleChange = (optionValue, checked) => {
    const newValue = checked
      ? [...(value || []), optionValue]
      : (value || []).filter((v) => v !== optionValue);
    onChange({ target: { value: newValue } });
  };

  const handleSelectAll = (checked) => {
    const newValue = checked ? options : [];
    onChange({ target: { value: newValue } });
  };

  return (
    <FormControl component="fieldset" {...props}>
      {label && <FormLabel component="legend">{label}</FormLabel>}
      <FormGroup sx={{ flexDirection: layout === 'horizontal' ? 'row' : 'column' }}>
        {showSelectAll && (
          <FormControlLabel
            control={(
              <Checkbox
                checked={(value || []).length === options.length}
                indeterminate={(value || []).length > 0 && (value || []).length < options.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            )}
            label="Select All"
          />
        )}
        {options.map((option) => (
          <FormControlLabel
            key={option}
            control={(
              <Checkbox
                checked={(value || []).includes(option)}
                onChange={(e) => handleChange(option, e.target.checked)}
              />
            )}
            label={option}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

export function EnhancedAutocomplete({
  value,
  onChange,
  options = [],
  placeholder,
  freeSolo = true,
  multiple = false,
  ...props
}) {
  return (
    <Autocomplete
      value={value}
      onChange={(e, newValue) => onChange({ target: { value: newValue } })}
      options={options}
      freeSolo={freeSolo}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
        />
      )}
      {...props}
    />
  );
}

export function FileUpload({
  onChange,
  accept = '*/*',
  multiple = false,
  maxSize = '10MB',
  showProgress = true,
  ...props
}) {
  const [files, setFiles] = React.useState([]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    onChange({ target: { value: selectedFiles } });
  };

  return (
    <Box>
      <input
        type="file"
        onChange={handleFileChange}
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        id="file-upload"
        {...props}
      />
      <label htmlFor="file-upload">
        <Button
          variant="outlined"
          component="span"
          startIcon={<UploadIcon />}
          fullWidth
        >
          Choose File
          {multiple ? 's' : ''}
        </Button>
      </label>
      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {files.map((file, index) => (
            <Chip
              key={index}
              label={`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
              onDelete={() => {
                const newFiles = files.filter((_, i) => i !== index);
                setFiles(newFiles);
                onChange({ target: { value: newFiles } });
              }}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export function DataGrid({
  value = [],
  onChange,
  columns = [],
  addRowText = 'Add Row',
  deleteRowText = 'Delete',
  minRows = 1,
  maxRows = 10,
  showRowNumbers = true,
  ...props
}) {
  const addRow = () => {
    if ((value || []).length < maxRows) {
      const newRow = {};
      columns.forEach((col) => {
        newRow[col.key] = '';
      });
      onChange({ target: { value: [...(value || []), newRow] } });
    }
  };

  const deleteRow = (index) => {
    if ((value || []).length > minRows) {
      const newValue = (value || []).filter((_, i) => i !== index);
      onChange({ target: { value: newValue } });
    }
  };

  const updateCell = (rowIndex, columnKey, cellValue) => {
    const newValue = [...(value || [])];
    newValue[rowIndex] = { ...newValue[rowIndex], [columnKey]: cellValue };
    onChange({ target: { value: newValue } });
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {showRowNumbers && <TableCell width={50}>#</TableCell>}
              {columns.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
              <TableCell width={50}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(value || []).map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {showRowNumbers && <TableCell>{rowIndex + 1}</TableCell>}
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    <TextField
                      size="small"
                      value={row[col.key] || ''}
                      onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
                      fullWidth
                      variant="standard"
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => deleteRow(rowIndex)}
                    disabled={(value || []).length <= minRows}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        startIcon={<AddIcon />}
        onClick={addRow}
        disabled={(value || []).length >= maxRows}
        sx={{ mt: 1 }}
      >
        {addRowText}
      </Button>
    </Box>
  );
}

export function GroupBox({
  label,
  children,
  collapsible = false,
  collapsed = false,
  border = true,
  ...props
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(collapsed);

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  if (border) {
    return (
      <Box {...props}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            cursor: collapsible ? 'pointer' : 'default',
          }}
          onClick={toggleCollapse}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {label}
          </Typography>
          {collapsible && (
            <IconButton size="small">
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          )}
        </Box>
        <Collapse in={!isCollapsed}>
          <Box>
            {children}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box {...props}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          cursor: collapsible ? 'pointer' : 'default',
        }}
        onClick={toggleCollapse}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {label}
        </Typography>
        {collapsible && (
          <IconButton size="small">
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        )}
      </Box>
      <Collapse in={!isCollapsed}>
        <Box>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

// Rich Text Editor (simplified version)
export function RichTextEditor({
  value,
  onChange,
  height = 200,
  placeholder = 'Enter rich text...',
  ...props
}) {
  return (
    <TextField
      multiline
      rows={Math.floor(height / 25)}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      variant="outlined"
      fullWidth
      {...props}
    />
  );
}

// Code Editor (simplified version)
export function CodeEditor({
  value,
  onChange,
  language = 'javascript',
  height = 150,
  placeholder = '// Enter code here...',
  showLineNumbers = true,
  ...props
}) {
  return (
    <TextField
      multiline
      rows={Math.floor(height / 25)}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      variant="outlined"
      fullWidth
      InputProps={{
        sx: {
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: '0.875rem',
        },
      }}
      {...props}
    />
  );
}

// Google Maps Address Picker Component
// Google Maps Component
function GoogleMapComponent({ center, zoom = 15, style }) {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);

  useEffect(() => {
    if (window.google && window.google.maps && center && center.lat && center.lng) {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat: center.lat, lng: center.lng },
        zoom,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      // Add a marker for the selected location
      new window.google.maps.Marker({
        position: { lat: center.lat, lng: center.lng },
        map: mapInstance,
        title: center.address,
      });

      setMap(mapInstance);
    }
  }, [center, zoom]);

  return (
    <div ref={mapRef} style={style} />
  );
}

export function GoogleMapsAddressPicker({
  label,
  value = '',
  onChange,
  placeholder = 'Search for an address...',
  apiKey,
  disabled = false,
  required = false,
  fullWidth = true,
  ...props
}) {
  const [searchValue, setSearchValue] = useState(value);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const autocompleteRef = useRef(null);

  // Initialize Google Maps API
  useEffect(() => {
    console.log('GoogleMapsAddressPicker - API Key available:', !!apiKey);
    console.log('GoogleMapsAddressPicker - API Key length:', apiKey ? apiKey.length : 0);
    console.log('GoogleMapsAddressPicker - window.google available:', !!window.google);

    if (!apiKey) {
      console.warn('Google Maps API key not provided. Address picker will work in basic mode.');
      return;
    }

    if (!window.google) {
      const script = document.createElement('script');
      const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      console.log('GoogleMapsAddressPicker - Loading Google Maps API from:', scriptUrl);
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('GoogleMapsAddressPicker - Google Maps API loaded successfully');
        setGoogleLoaded(true);
        initializeAutocomplete();
      };
      script.onerror = (error) => {
        console.error('GoogleMapsAddressPicker - Failed to load Google Maps API:', error);
        console.error('GoogleMapsAddressPicker - Script URL was:', scriptUrl);
        console.error('GoogleMapsAddressPicker - API Key length:', apiKey ? apiKey.length : 0);
        console.error('GoogleMapsAddressPicker - API Key starts with:', apiKey ? `${apiKey.substring(0, 10)}...` : 'undefined');
      };
      document.head.appendChild(script);
    } else {
      console.log('GoogleMapsAddressPicker - Google Maps API already loaded');
      setGoogleLoaded(true);
      initializeAutocomplete();
    }
  }, [apiKey]);

  const initializeAutocomplete = () => {
    if (autocompleteRef.current && window.google && window.google.maps) {
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const address = place.formatted_address;
            const location = {
              address,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              placeId: place.place_id,
            };
            setSelectedLocation(location);
            setSearchValue(address);
            onChange({ target: { value: address } });
            setShowMap(true);
          }
        });
      } catch (error) {
        console.error('Error initializing Google Maps autocomplete:', error);
      }
    }
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
    onChange({ target: { value } });
  };

  const handleClearLocation = () => {
    setSearchValue('');
    setSelectedLocation(null);
    setShowMap(false);
    onChange({ target: { value: '' } });
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        ref={autocompleteRef}
        label={label}
        placeholder={placeholder}
        value={searchValue}
        onChange={handleSearchChange}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        variant="outlined"
        InputProps={{
          endAdornment: (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selectedLocation && (
                <IconButton
                  size="small"
                  onClick={() => setShowMap(!showMap)}
                  title={showMap ? 'Hide Map' : 'Show Map'}
                >
                  <LocationOnIcon />
                </IconButton>
              )}
              {selectedLocation && (
                <IconButton
                  size="small"
                  onClick={handleClearLocation}
                  title="Clear Location"
                >
                  <ClearIcon />
                </IconButton>
              )}
            </Box>
          ),
        }}
        {...props}
      />

      {!apiKey && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
          ⚠️ Google Maps API key not configured. Address autocomplete will not work.
        </Typography>
      )}

      {showMap && selectedLocation && googleLoaded && (
        <Box sx={{
          mt: 2, height: 300, border: 1, borderColor: 'divider', borderRadius: 1,
        }}
        >
          <GoogleMapComponent
            center={selectedLocation}
            zoom={15}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
      )}

      {selectedLocation && (
        <Box sx={{
          mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1,
        }}
        >
          <Typography variant="caption" color="text.secondary">
            Selected:
            {' '}
            {selectedLocation.address}
          </Typography>
          {selectedLocation.lat && selectedLocation.lng && (
            <Typography variant="caption" display="block" color="text.secondary">
              Coordinates:
              {' '}
              {selectedLocation.lat.toFixed(6)}
              ,
              {' '}
              {selectedLocation.lng.toFixed(6)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// Enhanced widget definitions with proper categories
export const ENHANCED_WIDGET_CATEGORIES = {
  basic: [
    {
      id: 'text',
      type: 'text',
      label: 'Text Input',
      icon: '📝',
      description: 'Single line text input',
      defaultValue: '',
      category: 'basic',
    },
    {
      id: 'textarea',
      type: 'textarea',
      label: 'Text Area',
      icon: '📄',
      description: 'Multi-line text input',
      defaultValue: '',
      rows: 4,
      category: 'basic',
    },
    {
      id: 'number',
      type: 'number',
      label: 'Number',
      icon: '🔢',
      description: 'Numeric input field',
      defaultValue: 0,
      min: 0,
      max: 100,
      step: 1,
      category: 'basic',
    },
    {
      id: 'email',
      type: 'email',
      label: 'Email',
      icon: '📧',
      description: 'Email address input',
      defaultValue: '',
      pattern: '^[^@]+@[^@]+\\.[^@]+$',
      category: 'basic',
    },
    {
      id: 'password',
      type: 'password',
      label: 'Password',
      icon: '🔒',
      description: 'Password input field',
      defaultValue: '',
      minLength: 6,
      category: 'basic',
    },
    {
      id: 'checkbox',
      type: 'checkbox',
      label: 'Checkbox',
      icon: '☑️',
      description: 'Single checkbox',
      defaultValue: false,
      category: 'basic',
    },
  ],

  enhanced: [
    {
      id: 'phone',
      type: 'phone',
      label: 'Phone Input',
      icon: '📞',
      description: 'Formatted phone number input',
      defaultValue: '',
      mask: '(999) 999-9999',
      category: 'enhanced',
    },
    {
      id: 'currency',
      type: 'currency',
      label: 'Currency Input',
      icon: '💰',
      description: 'Currency input with prefix',
      defaultValue: 0,
      prefix: '$',
      step: 0.01,
      category: 'enhanced',
    },
    {
      id: 'slider',
      type: 'slider',
      label: 'Slider',
      icon: '🎚️',
      description: 'Range slider input',
      defaultValue: 50,
      min: 0,
      max: 100,
      step: 1,
      showValue: true,
      marks: false,
      category: 'enhanced',
    },
    {
      id: 'rating',
      type: 'rating',
      label: 'Rating',
      icon: '⭐',
      description: 'Star rating input',
      defaultValue: 0,
      max: 5,
      allowHalf: true,
      showText: true,
      category: 'enhanced',
    },
    {
      id: 'color',
      type: 'color',
      label: 'Color Picker',
      icon: '🎨',
      description: 'Color selection input',
      defaultValue: '#1976d2',
      swatches: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3'],
      category: 'enhanced',
    },
    {
      id: 'switch',
      type: 'switch',
      label: 'Toggle Switch',
      icon: '🔄',
      description: 'Toggle switch input',
      defaultValue: false,
      size: 'medium',
      color: 'primary',
      category: 'enhanced',
    },
    {
      id: 'address',
      type: 'address',
      label: 'Address Picker',
      icon: '🗺️',
      description: 'Google Maps address selection with autocomplete',
      defaultValue: '',
      placeholder: 'Search for an address...',
      category: 'enhanced',
    },
  ],

  advanced: [
    {
      id: 'file',
      type: 'file',
      label: 'File Upload',
      icon: '📁',
      description: 'File upload input',
      defaultValue: [],
      accept: '*/*',
      multiple: false,
      maxSize: '10MB',
      showProgress: true,
      category: 'advanced',
    },
    {
      id: 'autocomplete',
      type: 'autocomplete',
      label: 'Autocomplete',
      icon: '🔍',
      description: 'Searchable dropdown with autocomplete',
      defaultValue: '',
      options: ['Option 1', 'Option 2', 'Option 3'],
      freeSolo: true,
      multiple: false,
      category: 'advanced',
    },
    {
      id: 'checkboxgroup',
      type: 'checkboxgroup',
      label: 'Checkbox Group',
      icon: '☑️',
      description: 'Multiple checkbox selection',
      defaultValue: [],
      options: ['Option 1', 'Option 2', 'Option 3'],
      layout: 'vertical',
      showSelectAll: false,
      category: 'advanced',
    },
    {
      id: 'grid',
      type: 'grid',
      label: 'Data Grid',
      icon: '📊',
      description: 'Editable data table',
      defaultValue: [],
      columns: [],
      addRowText: 'Add Row',
      deleteRowText: 'Delete',
      minRows: 1,
      maxRows: 10,
      showRowNumbers: true,
      category: 'advanced',
    },
    {
      id: 'richtext',
      type: 'richtext',
      label: 'Rich Text Editor',
      icon: '📝',
      description: 'Rich text editing interface',
      defaultValue: '',
      height: 200,
      toolbar: ['bold', 'italic', 'underline'],
      category: 'advanced',
    },
    {
      id: 'code',
      type: 'code',
      label: 'Code Editor',
      icon: '💻',
      description: 'Code editing with syntax highlighting',
      defaultValue: '',
      language: 'javascript',
      height: 150,
      showLineNumbers: true,
      category: 'advanced',
    },
    {
      id: 'address',
      type: 'address',
      label: 'Address Picker',
      icon: '🗺️',
      description: 'Google Maps address selection with autocomplete',
      defaultValue: '',
      placeholder: 'Search for an address...',
      category: 'advanced',
    },
  ],

  datetime: [
    {
      id: 'date',
      type: 'date',
      label: 'Date',
      icon: '📅',
      description: 'Date picker input',
      defaultValue: '',
      category: 'datetime',
    },
    {
      id: 'datetime',
      type: 'datetime',
      label: 'Date Time',
      icon: '📅',
      description: 'Date and time picker',
      defaultValue: '',
      category: 'datetime',
    },
    {
      id: 'time',
      type: 'time',
      label: 'Time',
      icon: '🕐',
      description: 'Time picker input',
      defaultValue: '',
      category: 'datetime',
    },
  ],

  selection: [
    {
      id: 'combobox',
      type: 'combobox',
      label: 'Dropdown',
      icon: '📋',
      description: 'Single selection dropdown',
      defaultValue: '',
      options: ['Option 1', 'Option 2', 'Option 3'],
      multiple: false,
      category: 'selection',
    },
    {
      id: 'listbox',
      type: 'listbox',
      label: 'List Box',
      icon: '📋',
      description: 'Multi-selection list',
      defaultValue: [],
      options: ['Option 1', 'Option 2', 'Option 3'],
      multiple: true,
      category: 'selection',
    },
    {
      id: 'radiobuttons',
      type: 'radiobuttons',
      label: 'Radio Buttons',
      icon: '🔘',
      description: 'Single choice radio buttons',
      defaultValue: '',
      options: ['Option 1', 'Option 2', 'Option 3'],
      category: 'selection',
    },
  ],

  layout: [
    {
      id: 'groupbox',
      type: 'groupbox',
      label: 'Group Box',
      icon: '📦',
      description: 'Container for grouping fields',
      defaultValue: '',
      fields: [],
      collapsible: false,
      collapsed: false,
      border: true,
      category: 'layout',
    },
  ],
};

// Flatten all widgets for easy access
export const ALL_ENHANCED_WIDGETS = Object.values(ENHANCED_WIDGET_CATEGORIES).flat();

export default {
  PhoneInput,
  CurrencyInput,
  EnhancedSlider,
  EnhancedRating,
  ColorPicker,
  ToggleSwitch,
  CheckboxGroup,
  EnhancedAutocomplete,
  FileUpload,
  DataGrid,
  GroupBox,
  RichTextEditor,
  CodeEditor,
  GoogleMapsAddressPicker,
};
